import React, { useState } from 'react';
import styles from './ImportPreview.module.css';

// ---- Preview mode (resolution categories) ----
const LABELS = {
  New: 'New',
  Add: 'Add',
  Update: 'Update',
  NotImported: 'Skip',
  NoChange: 'No change',
  Block: 'Block',
};

const BADGE = {
  New: styles.badgeNew,
  Add: styles.badgeAdd,
  Update: styles.badgeUpdate,
  NotImported: styles.badgeNotImported,
  NoChange: styles.badgeNoChange,
  Block: styles.badgeBlock,
};

// Display order for the summary tiles. "No change" sits last — the quiet, expected outcome.
// Mirrors OUTCOME_ORDER (the result/history order): the two "added" preview categories (New + Add) lead, then Update,
// No change, Skip (= Skipped) and Block. There's no Failed in a dry-run preview.
const ORDER = ['New', 'Add', 'Update', 'NoChange', 'NotImported', 'Block'];

// ---- Meet-registration preview (variant='meet') ----
// The admin's questions ARE the tiles: how many am I ADDING to this meet, how many UPDATING, how many ALREADY
// REGISTERED & unchanged, and what's BLOCKED. So the three "new registration" categories the resolver produces
// (Register = host athlete, AddVisitor = existing platform athlete, NewVisitor = brand-new athlete) collapse into ONE
// "Add" tile/filter/label — the host-vs-visitor and new-vs-existing nuance lives in the per-row detail + badge COLOUR
// (green = new to SplitFast, blue = already on it), which the admin can glance at but doesn't have to. MEET_BADGE is
// therefore keyed by the RAW category (for colour); MEET_LABELS / MEET_BADGE_TEXT / MEET_ORDER use the collapsed key.
const MEET_LABELS = {
  Add: 'Add',
  Update: 'Update',
  NoChange: 'Already registered',
  Block: 'Block',
  Failed: 'Failed', // result mode only
};

// Past-tense tile labels for the persisted last-import (history) view — these describe what ALREADY happened, so
// "Add"/"Update"/"Block" read better as "Added"/"Updated"/"Blocked". The live preview and fresh result keep MEET_LABELS.
const MEET_HISTORY_LABELS = {
  Add: 'Added',
  Update: 'Updated',
  NoChange: 'Already registered',
  Block: 'Blocked',
  Failed: 'Failed',
};

// Badge text is single-word (two-word labels wrap in the pill). The unchanged tile reads "Already registered" but its
// row badge is the compact "Registered".
const MEET_BADGE_TEXT = {
  Add: 'Add',
  Update: 'Update',
  NoChange: 'Registered',
  Block: 'Block',
  Failed: 'Failed', // result mode only
};

const MEET_BADGE = {
  Register: styles.badgeRegister, // orange — your own club athlete
  AddVisitor: styles.badgeAdd, // blue — existing platform athlete, added as a visitor
  NewVisitor: styles.badgeNew, // green — brand-new athlete, created as a visitor
  Update: styles.badgeUpdate,
  NoChange: styles.badgeNoChange,
  Block: styles.badgeBlock,
};

const MEET_ORDER = ['Add', 'Update', 'NoChange', 'Block'];

// Collapse the three "new registration" categories into the single "Add" group used for the tile, filter and label.
const meetCategoryKey = (category) =>
  category === 'Register' || category === 'AddVisitor' || category === 'NewVisitor'
    ? 'Add'
    : category;

// ---- Meet RESULT mode (apply outcomes) ----
// Keyed by the apply OUTCOME. The three "added" outcomes collapse to the Add group; badge colour still orange (host) /
// blue (existing platform) / green (brand new); Failed/Blocked are red.
const MEET_OUTCOME_BADGE = {
  Registered: styles.badgeRegister,
  VisitorAdded: styles.badgeAdd,
  VisitorCreated: styles.badgeNew,
  Updated: styles.badgeUpdate,
  NoChange: styles.badgeNoChange,
  Blocked: styles.badgeBlock,
  Failed: styles.badgeFailed,
};

const MEET_RESULT_ORDER = ['Add', 'Update', 'NoChange', 'Block', 'Failed'];

const meetOutcomeKey = (o) =>
  o === 'Registered' || o === 'VisitorAdded' || o === 'VisitorCreated'
    ? 'Add'
    : o === 'Updated'
    ? 'Update'
    : o === 'Blocked'
    ? 'Block'
    : o; // 'NoChange' | 'Failed'

// The athlete's membership type in THIS club, in admin terms (Full → Member).
const inClubTypeLabel = (t) =>
  t === 'Full' ? 'Member' : t === 'Guest' ? 'Guest' : t === 'Visitor' ? 'Visitor' : 'Athlete';

// The per-row detail line for an actionable (non-block) meet row — composed on the client so it can bold the membership
// type / "Visitor" and surface reactivation. Block rows fall back to the server's detailed reason + diffs.
const meetActionLine = (r) => {
  switch (r.category) {
    case 'Register': {
      const type = inClubTypeLabel(r.inClubMembershipType);
      return r.reactivating ? (
        <>
          <strong>{type}</strong> in your club — will be reactivated for this meet.
        </>
      ) : (
        <>
          <strong>{type}</strong> in your club.
        </>
      );
    }
    case 'AddVisitor':
      return r.representingClub ? (
        <>
          Found on SplitFast: adding as a <strong>Visitor</strong> representing{' '}
          <strong>{r.representingClub}</strong> club.
        </>
      ) : (
        <>
          Found on SplitFast: adding as a <strong>Visitor</strong>.
        </>
      );
    case 'NewVisitor':
      return r.representingClub ? (
        <>
          New athlete, adding as a <strong>Visitor</strong> representing{' '}
          <strong>{r.representingClub}</strong> club.
        </>
      ) : (
        <>
          New athlete, adding as a <strong>Visitor</strong>.
        </>
      );
    case 'Update': {
      const type = inClubTypeLabel(r.inClubMembershipType);
      return r.reactivating ? (
        <>
          <strong>{type}</strong> in your club — will be reactivated for this meet.
        </>
      ) : (
        <>
          <strong>{type}</strong> in your club, already registered — events updated.
        </>
      );
    }
    case 'NoChange': {
      const type = inClubTypeLabel(r.inClubMembershipType);
      return (
        <>
          <strong>{type}</strong> in your club, already registered — no change to events.
        </>
      );
    }
    default:
      return r.reason;
  }
};

// ---- Result mode (apply outcomes) ----
// AddedNew (brand-new athlete) and AddedExisting (existing platform athlete linked) collapse to a single "Added"
// tile / filter / list, with both labelled "Added" — the new-vs-existing distinction lives in each row's description.
// The per-row badge COLOUR still differs though (green = new, blue = existing), so OUTCOME_BADGE is keyed by the raw
// outcome while OUTCOME_LABELS / OUTCOME_ORDER use the collapsed key. See outcomeKey().
const OUTCOME_LABELS = {
  Added: 'Added',
  Updated: 'Updated',
  Failed: 'Failed',
  Blocked: 'Blocked',
  Skipped: 'Skipped',
  NoChange: 'No change',
};

const OUTCOME_BADGE = {
  AddedNew: styles.badgeNew, // green — brand-new athlete
  AddedExisting: styles.badgeAdd, // blue — existing platform athlete linked into the club
  Updated: styles.badgeUpdate,
  Failed: styles.badgeFailed,
  Blocked: styles.badgeBlock,
  Skipped: styles.badgeNotImported,
  NoChange: styles.badgeNoChange,
};

const OUTCOME_ORDER = ['Added', 'Updated', 'NoChange', 'Skipped', 'Blocked', 'Failed'];

// Collapse the two "added" outcomes into the single "Added" group used for the tile, filter and badge.
const outcomeKey = (outcome) =>
  outcome === 'AddedNew' || outcome === 'AddedExisting' ? 'Added' : outcome;

// "29 Nov 2025, 7:30 am" in the BROWSER's local timezone. The server stores UTC, but the value comes back without a
// timezone designator (no trailing Z / ±offset), which JS would otherwise parse as local — so we tag it UTC first,
// then toLocale* converts to local. Null for a malformed/absent timestamp.
const formatAppliedAt = (iso) => {
  if (!iso) return null;
  const hasTz = typeof iso === 'string' && /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const time = d
    .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
  return `${date}, ${time}`;
};

// "First Last (email)", or just the email when there's no contact name (member rows always carry an email).
const contactLabel = (row) => {
  const name = [row.primaryContactFirstName, row.primaryContactLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const email = row.primaryContactEmail || '';
  if (name && email) return `${name} (${email})`;
  return name || email || '—';
};

// Renders the member-import report — both the dry-run PREVIEW (per-row resolution, no writes) and the apply RESULT
// (per-row outcome) with one component, so the two read substantially the same. mode='preview' (default) keys off the
// resolved category; mode='result' keys off the applied outcome and hides the no-change / skipped rows by default,
// surfacing every changed and blocked row. Purely presentational.
const ImportPreview = ({ data, mode = 'preview', summaryOnly = false, variant = 'member' }) => {
  const isResult = mode === 'result';
  const isMeet = variant === 'meet';
  // Primary view hides the quiet rows: preview hides "No change"; result hides "No change" + "Skipped".
  const [filter, setFilter] = useState(isResult ? 'Changes' : 'Review');

  if (!data) {
    return null;
  }

  // File-level rejection (missing header, empty file, over the cap, contradictory dates) — no rows to show.
  if (!data.fileAccepted) {
    return (
      <div className={styles.container}>
        <div className={styles.fileError}>
          <h3 className={styles.fileErrorTitle}>File not accepted</h3>
          <ul className={styles.fileErrorList}>
            {(data.fileErrors || []).map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const rows = data.rows || [];

  const labels = isMeet ? MEET_LABELS : isResult ? OUTCOME_LABELS : LABELS;
  const badges = isResult
    ? isMeet
      ? MEET_OUTCOME_BADGE
      : OUTCOME_BADGE
    : isMeet
    ? MEET_BADGE
    : BADGE;
  const order = isResult
    ? isMeet
      ? MEET_RESULT_ORDER
      : OUTCOME_ORDER
    : isMeet
    ? MEET_ORDER
    : ORDER;
  const keyOf = (r) =>
    isResult
      ? isMeet
        ? meetOutcomeKey(r.outcome)
        : outcomeKey(r.outcome)
      : isMeet
      ? meetCategoryKey(r.category)
      : r.category;

  const counts = isResult
    ? isMeet
      ? {
          // Add collapses the three "added" outcomes (registered / visitor-added / visitor-created).
          Add:
            (data.registeredCount || 0) +
            (data.visitorAddedCount || 0) +
            (data.visitorCreatedCount || 0),
          Update: data.updatedCount,
          Failed: data.failedCount,
          Block: data.blockedCount,
          NoChange: data.noChangeCount,
        }
      : {
          Added: (data.addedNewCount || 0) + (data.addedExistingCount || 0),
          Updated: data.updatedCount,
          Failed: data.failedCount,
          Blocked: data.blockedCount,
          Skipped: data.skippedCount,
          NoChange: data.noChangeCount,
        }
    : isMeet
    ? {
        // Add collapses the three "new registration" categories.
        Add:
          (data.registerCount || 0) +
          (data.addVisitorCount || 0) +
          (data.newVisitorCount || 0),
        Update: data.updateCount,
        NoChange: data.noChangeCount,
        Block: data.blockCount,
      }
    : {
        New: data.newCount,
        Add: data.addCount,
        Update: data.updateCount,
        NotImported: data.notImportedCount,
        Block: data.blockCount,
        NoChange: data.noChangeCount,
      };

  const primaryKey = isResult ? 'Changes' : 'Review';
  const primaryLabel = isResult ? 'Changes & blocks' : 'Needs review';
  const primaryCount = isResult
    ? (data.totalRows || 0) - (data.noChangeCount || 0) - (data.skippedCount || 0)
    : (data.totalRows || 0) - (data.noChangeCount || 0);

  // RESULT views (the applied-confirmation AND the persisted history) describe what ALREADY happened, so the meet tiles
  // read past tense ("Added"/"Updated"/"Blocked"). Preview keeps the present-tense MEET_LABELS ("what WILL happen"); the
  // member result labels (OUTCOME_LABELS) are already past tense.
  const tileLabels = isResult && isMeet ? MEET_HISTORY_LABELS : labels;

  // Persisted "last import" view — category tiles only (no per-row table, no filtering). Same tiles + counts as the
  // result report so the reload reads the same; the rows themselves aren't stored.
  if (summaryOnly) {
    const appliedAtLabel = formatAppliedAt(data.appliedAt);
    return (
      <div className={styles.container}>
        <div className={styles.banner}>
          <span>
            <strong>Last import:</strong>{' '}
            {appliedAtLabel ? `${appliedAtLabel} · ` : ''}
            {data.totalRows} rows
          </span>
        </div>
        <div className={styles.summary}>
          {/* The aggregate "Changes & blocks" tile is intentionally omitted from the persisted last-import view — its
              meaning wasn't clear; the per-category tiles below already say exactly what happened. */}
          {order.map((c) => (
            <div key={c} className={`${styles.tile} ${styles.tileStatic}`}>
              <div className={styles.tileValue}>{counts[c] ?? 0}</div>
              <div className={styles.tileLabel}>{tileLabels[c]}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isPrimary = (r) =>
    isResult
      ? r.outcome !== 'NoChange' && r.outcome !== 'Skipped'
      : r.category !== 'NoChange';

  const hasWarning = (r) => r.warnings && r.warnings.length > 0;

  // 'Check' is an OVERLAP filter (a warned row is still an Add/Update/etc.), not one of the partition tiles.
  const filtered =
    filter === 'Check'
      ? rows.filter(hasWarning)
      : filter === primaryKey
      ? rows.filter(isPrimary)
      : rows.filter((r) => keyOf(r) === filter);

  return (
    <div className={styles.container}>
      <div className={styles.banner}>
        <span>
          {isResult ? <strong>Import complete</strong> : <strong>{data.totalRows} rows</strong>}
        </span>
        {isResult && (
          <span>
            <strong>{data.totalRows}</strong> rows
          </span>
        )}
        {data.sourceFormat === 'Sport80' && (
          <span>
            Detected a <strong>Sport80 member report</strong> — columns matched automatically.
          </span>
        )}
        <span>
          Date of birth format detected as:{' '}
          <strong>{data.detectedDateOrder || 'n/a'}</strong>
        </span>
        {data.dateFormatAmbiguous && (
          <span className={styles.warn}>
            Date format is ambiguous — set the format and re-preview.
          </span>
        )}
        {isResult && (data.failedCount || 0) > 0 && (
          <span className={styles.warn}>
            {data.failedCount} row{data.failedCount === 1 ? '' : 's'} could not be applied — see below.
          </span>
        )}
      </div>

      {/* The summary panels ARE the filter — click one to show that group (active panel is highlighted). The aggregate
          "Changes & blocks" tile is shown only in PREVIEW; the applied-result hides it (matching the history view) and
          defaults to showing all changed rows, with the per-category tiles to narrow down. */}
      <div className={styles.summary}>
        {!isResult && (
          <button
            type="button"
            className={`${styles.tile} ${filter === primaryKey ? styles.tileActive : ''}`}
            onClick={() => setFilter(primaryKey)}
          >
            <div className={styles.tileValue}>{primaryCount}</div>
            <div className={styles.tileLabel}>{primaryLabel}</div>
          </button>
        )}
        {order.map((c) => (
          <button
            key={c}
            type="button"
            className={`${styles.tile} ${filter === c ? styles.tileActive : ''}`}
            // Toggle: clicking the active category clears back to the primary (all-changes) view. Matters most in the
            // applied-result, where the "Changes & blocks" tile is hidden, so this is the only way back to "show all".
            onClick={() => setFilter(filter === c ? primaryKey : c)}
          >
            <div className={styles.tileValue}>{counts[c] ?? 0}</div>
            <div className={styles.tileLabel}>{tileLabels[c]}</div>
          </button>
        ))}
        {/* Overlap filter (not a partition tile): rows carrying a non-blocking data-quality warning. */}
        {isMeet && (data.warningCount || 0) > 0 && (
          <button
            type="button"
            className={`${styles.tile} ${styles.tileWarn} ${filter === 'Check' ? styles.tileActive : ''}`}
            onClick={() => setFilter('Check')}
          >
            <div className={styles.tileValue}>{data.warningCount}</div>
            <div className={styles.tileLabel}>⚠ Check</div>
          </button>
        )}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Row</th>
              <th>Athlete</th>
              <th>Bib</th>
              <th>Outcome</th>
              <th>Reason &amp; differences</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const row = r.row || {};
              const key = keyOf(r);
              const isBlockLike = key === 'Block' || key === 'Blocked' || key === 'Failed';
              // Only a brand-new athlete/visitor creates a primary contact; an existing-platform link reuses one.
              const showContact = isResult
                ? isMeet
                  ? r.outcome === 'VisitorCreated'
                  : r.outcome === 'AddedNew'
                : isMeet
                ? r.category === 'NewVisitor'
                : r.category === 'New';
              return (
                <tr
                  key={row.rowNumber}
                  className={isBlockLike ? styles.blockRow : undefined}
                >
                  <td>{row.rowNumber}</td>
                  <td>
                    <div className={styles.name}>
                      {row.firstName} {row.lastName}
                    </div>
                    <div className={styles.sub}>
                      DOB {row.dateOfBirth || row.dateOfBirthRaw || '—'} ·{' '}
                      {row.gender || '?'}
                    </div>
                  </td>
                  <td>{row.bibNumber ?? '—'}</td>
                  <td>
                    {/* Colour by the raw category/outcome (green=new, blue=existing); label by the collapsed key. */}
                    <span
                      className={`${styles.badge} ${badges[isResult ? r.outcome : r.category] || ''}`}
                    >
                      {isMeet ? MEET_BADGE_TEXT[key] || key : labels[key] || key}
                    </span>
                  </td>
                  <td>
                    {isMeet && !isBlockLike ? (
                      <>
                        <div className={styles.reason}>{meetActionLine(r)}</div>
                        {showContact && (
                          <div className={styles.contact}>
                            {r.primaryContactExists
                              ? `Added to existing primary contact: ${contactLabel(row)}`
                              : `Primary contact (also added): ${contactLabel(row)}`}
                          </div>
                        )}
                        <div className={styles.events}>
                          {r.mappedEvents && r.mappedEvents.length > 0
                            ? `Events: ${r.mappedEvents.join(', ')}`
                            : 'No events listed'}
                        </div>
                        {(r.warnings || []).map((w, i) => (
                          <div key={i} className={styles.warning}>
                            ⚠ {w}
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <div className={styles.reason}>{r.reason}</div>
                        {showContact && (
                          <div className={styles.contact}>
                            {r.primaryContactExists
                              ? `Added to existing primary contact: ${contactLabel(row)}`
                              : `Primary contact (also added): ${contactLabel(row)}`}
                          </div>
                        )}
                        {r.diffs && r.diffs.length > 0 && (
                          <table className={styles.diffTable}>
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>CSV</th>
                                <th>Portal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.diffs.map((d, i) => (
                                <tr key={i}>
                                  <td>{d.field}</td>
                                  <td className={styles.diffCsv}>{d.csv || '(empty)'}</td>
                                  <td className={styles.diffPortal}>
                                    {d.portal || '(empty)'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {r.matchedSummary && (
                          <div className={styles.matched}>Matched: {r.matchedSummary}</div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  No rows in this category.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportPreview;
